import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNow } from '../../../../hooks/useNow';
import { getOfferExpiryMs, isOfferPastExpiry, type OfferItem } from '../types';

/**
 * Live clock for an offer. Ticks on an interval and exactly at the expiration
 * instant; when a pending offer crosses its expiry while on screen, the offer
 * is refetched so the backend's `expired` status replaces the client-side guess.
 */
export function useOfferNow(
  offer: Pick<OfferItem, 'id' | 'status' | 'expirationDate'> | undefined,
  intervalMs = 30_000,
): number {
  const queryClient = useQueryClient();
  const expiryMs = offer && offer.status === 'pending' ? getOfferExpiryMs(offer) : null;
  const now = useNow(intervalMs, expiryMs);
  const pastExpiry = !!offer && offer.status === 'pending' && isOfferPastExpiry(offer, now);
  const wasPastExpiry = useRef(pastExpiry);

  useEffect(() => {
    if (pastExpiry && !wasPastExpiry.current && offer?.id) {
      queryClient.invalidateQueries({ queryKey: ['offers', offer.id] });
      queryClient.invalidateQueries({ queryKey: ['offers', 'list'] });
    }
    wasPastExpiry.current = pastExpiry;
  }, [pastExpiry, offer?.id, queryClient]);

  return now;
}
